import { LineItemLabel, LineItemWithDivision } from '../glossary/glossary.type';
import { UserDTO } from '../user-management/auth.type';
import { FileUploadDTO, NoteDTO, State } from '../../common/utils/util.type';
import { FilterConfig, FilterConfigLookupOption } from '../custom-filter-panel/DynamicFilters';
import { CSSProperties } from 'react';

export const warningColor = 'rgb(251, 140, 0)';

export type TagDTO = {
  id: string;
  name: string;
  description?: string;
};

export type ProjectTagDTO = {
  id: string;
  tag: TagDTO;
  details?: string;
};

export type City = {
  name: string;
  state: State;
  county?: string | null;
};

export type StructuredProjectAddressWithCity = {
  street: string;
  city: City;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
};

export type ProjectAddressSturctured = {
  street: string;
  city: string;
  state?: State;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
};
export type ProjectAddress = {
  street: string;
  city: string;
  state?: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
};
export type PersonDTO = {
  name: string;
  address: ProjectAddress;
  company?: string;
  loanOfficerName?: string;
};

export interface ProjectItem {
  id: string;
  projectTitle: string;
  projectNumber: string;
}

export type ProjectData = {
  projectSummary: ProjectSummary;
  projectDraws: ProjectLineItemsAndSummary[];
  notes?: NoteDTO[];
};

export type ProjectVersion = ProjectSummary & {
  drawsAndLineItems: ProjectLineItemsAndSummary[];
};
export interface SaveProjectAnalysisDTO {
  projectId: string;
  metadata: ProjectMetadataDTO;
  createdLineItems?: ProjectLineItemDTO[];
  editedLineItems?: ProjectLineItemDTO[];
  deletedLineItemIds?: string[];
}
export interface ProjectMetadataDTO {
  name: string;
  grossBuildingSF?: number;
  netRentableSF?: number;
  unitCount?: number;
  buildingCount?: number;
  storyCount?: number;
  siteAcreage?: number;
}

export const ProjectTags = {
  Draft: 'Draft',
  DataMissing: 'Data Missing',
  CautionNeeded: 'Caution Needed',
  BadBudget: 'Bad Budget',
  BadProject: 'Bad Project',
} as const;

export const RuleBasedTags = [ProjectTags.DataMissing, ProjectTags.CautionNeeded];
export const ManualTagsWithRequiredNote = [ProjectTags.BadBudget, ProjectTags.BadProject];
export const AllProjectTags = [...Object.values(ProjectTags)];

export interface TagConfig {
  color: CSSProperties['color'];
  backgroundColor: CSSProperties['backgroundColor'];
  getTooltipContent: (project: ProjectSummary) => React.ReactNode;
}

export type ProjectSummary = {
  projectId: string;
  projectTitle: string;
  projectNumber: string;
  projectAddress: ProjectAddress;
  budget: number;
  unassignedBudget: number;
  status: string;
  tags: ProjectTagDTO[];
  contractor: string;
  loanOfficerName: string;
  observer: UserDTO;
  monitoringFee: string;
  monitoringType: string;
  planOrCostFee: string;
  planOrCostInvDate: string;
  siteArea: string;
  storyCount: string;
  buildingCount: string;
  unitCount: string;
  elevatorCount: string;
  otherCostFactor: string;
  grossBuildingArea: string;
  netArea: string;
  planOrCostReportDate: string;
  commencementDate: string;
  originalCompletionDateEstimated: string;
  revisedCompletionDate: string;
  cdccEstimatedCompletionDate: string;
  bankLoanExpirationDate: string;
  singleChangeOrderThreshold?: number;
  aggregateChangeOrderThreshold?: number;
  constructionType: string;
  electrical: string;
  exteriorWalls: string;
  foundation: string;
  hvacScope: string;
  hvacType: string;
  plumbing: string;
  projectType: string;
  roof: string;
  superStructure: string;
  uniqueCostFactors1: string;
  uniqueCostFactors2: string;
  uniqueCostFactors3: string;
  windows: string;
  reviewingSubcontractorLienWaivers?: boolean | null;
  reviewingConstructionMeetingMinutes?: boolean | null;
  reviewingPotentialChangeOrders?: boolean | null;
  reviewingPlanOrCost?: boolean | null;
  networkerName: string | null;
  networkerStatus: string | null;
  client: PersonDTO;
  borrower: PersonDTO;
  coverImage?: FileUploadDTO;
  previewCoverImage?: FileUploadDTO;
  createdOn?: string;
  updatedOn?: string;
  createdBy?: UserDTO;
  updatedBy?: UserDTO;
  errors?: FileUploadErrorDTO[];
  isEdited?: boolean;
  limitedReportId?: string;
  magicReportId?: string;
  missingFieldWarnings?: MissingFieldWarningDTO[];
};

export type ProjectPreviousFundingDTO = {
  id: string;
  applicationNumber?: string;
  applicationDate?: string;
  amountFunded?: number;
  receivedByContractor?: number;
  approvedAmount?: number;
  retainage?: number;
  percentRequested?: number;
  percentComplete?: number;
  totalContractAmount?: number;
  displayOrder: number;
  comment?: string;
  reviewedByCDCC?: boolean;
  isDiscrepancy?: boolean;
};

export interface CoverImageBlobsDTO {
  previewCoverImageBlobName: string;
  coverImageBlobName: string;
}

export type ProjectVersionInfo = {
  id: string;
  projectId: string;
  createdBy: UserDTO;
  createdOn: string;
};

export type FileUploadErrorDTO = {
  draw?: string;
  fieldName: string;
  lineItem?: string;
  message: string;
};

// A field the Magic Report relies on that's currently missing on this project (or its latest
// draw) — shown as an informational "Warnings" banner on the Project page, separate from
// FileUploadErrorDTO. Dismissing one persists via updateHiddenFieldWarnings and is scoped to
// fieldKey; it naturally reappears if the underlying field goes missing again later.
export type MissingFieldWarningDTO = {
  fieldKey: string;
  draw?: string;
  message: string;
};

export type DrawLineItemsAndSummary = {
  title?: string;
  lineItems?: ProjectLineItemDTO[];
  subtotals?: ProjectLineItemSubtotalDTO[];
  budgetSummary?: BudgetSummaryDTO;
  drawSummary?: DrawSummary;
  payappId?: string;
};

export type DrawSummary = {
  id: string; // Add id
  name: string; // Add name
  applicationNumber: string;
  periodBeginning: string;
  periodEnding: string;
  inputRetainagePercentage?: number;
  previousFundings?: ProjectPreviousFundingDTO[];
  isEnabled?: boolean;
};

// Assuming you have an array of these draws
export type ProjectDraws = DrawLineItemsAndSummary[];

export type ProjectLineItemsAndSummary = {
  lineItems?: ProjectLineItemDTO[];
  subtotals?: ProjectLineItemSubtotalDTO[];
  budgetSummary?: BudgetSummaryDTO;
  projectDrawSummary: DrawSummary;
  payappId?: string;
  isCreated?: boolean;
  isEdited?: boolean;
  isDeleted?: boolean;
  notes?: NoteDTO[];
  // Transient client-side state: reanchor/delete instructions for subtotals whose anchor
  // line item was deleted locally, accumulated until the next save so they can be forwarded
  // to the backend alongside the deleted line items.
  pendingSubtotalReanchors?: SubtotalReanchorDTO[];
};

export type ProjectLineItemSubtotalDTO = {
  id: string;
  projectDrawId: string;
  anchorDrawLineItemId: string;
  anchorSerialNumber: number;
  label: string;
  // How many line items (counting backward from the anchor, inclusive) this subtotal sums.
  // null/undefined means "use the default range" (since the previous subtotal, or top of table).
  range?: number | null;
  originalBudget: number;
  changeOrder: number;
  reallocation: number;
  revisedBudget: number;
  previousApplication: number;
  completeThisPeriod: number;
  storedThisPeriod: number;
  cdccApproved: number;
  completedStoredToDate: number;
  percentageComplete: number;
  balanceToComplete: number;
  retainage: number;
  quantity: number;
  // Client-side only, mirroring ProjectLineItemDTO.isDeleted — e.g. a payapp merge marks the
  // project's pre-existing subtotals deleted since the payapp's own subtotals fully replace
  // them. Filtered from display and forwarded as a delete request on the next save.
  isDeleted?: boolean;
};

// Frontend-only instruction, flushed on the next save, telling the backend how to re-anchor (or
// delete) a subtotal — either because its anchor DrawLineItem was deleted, or because the user
// changed its range via the checkbox range editor (see useSubtotalRangeEditor in subtotal.util.tsx).
export type SubtotalReanchorDTO = {
  subtotalId: string;
  newAnchorDrawLineItemId: string | null;
  // Null means "don't touch" (the delete-triggered reanchor case never sets this).
  range?: number | null;
};

export type DrawLineItemNoteDTO = {
  noteContent: DrawLineItemNoteContentDTO[];
};

export type ManualOverridesDTO = {
  retainage?: boolean;
};

export type DrawLineItemNoteContentDTO = {
  field: string;
  payappValue: number;
  projectValue: number;
};

export type ProjectLineItemDTO = {
  id: string;
  serialNumber?: number;
  description?: string;
  originalBudget: number;
  changeOrder: number;
  reallocation: number;
  revisedBudget: number;
  previousApplication: number;
  completeThisPeriod: number;
  storedThisPeriod: number;
  cdccApproved: number;
  completedStoredToDate: number;
  percentageComplete: number;
  balanceToComplete: number;
  retainage: number;
  lineItem: LineItemWithDivision;
  projectDrawId: string;
  projectDrawName: string;
  quantity: number;
  isIncluded: boolean;
  isCreated?: boolean;
  isEdited?: boolean;
  isDeleted?: boolean;
  exitingAndNotMatched?: boolean; //incase of payapp upload
  updateDivisionGlobally?: boolean;
  divisionId?: string | undefined | null;
  payappRow?: Partial<ProjectLineItemDTO>;
  projectRow?: Partial<ProjectLineItemDTO>; //project row for previous month
  note?: DrawLineItemNoteDTO;
  labels?: LineItemLabel[];
  manualOverrides?: ManualOverridesDTO;
  // Fuzzy matching and grouping properties
  groupId?: string;
  matchType?: 'exact' | 'fuzzy' | 'none';
  fuzzyScore?: number;
  budgetMatch?: boolean;
  // Set on synthetic display rows built from a ProjectLineItemSubtotalDTO so the grid can
  // reuse ProjectLineItemDTO column renderers for subtotal rows with minimal special-casing.
  __rowType?: 'subtotal';
  __subtotalId?: string;
  // Payapp-merge subtotal tracing (see Payapp.util.tsx mergePayappIntoProjectDraw / PayappProcessFooter.tsx).
  // __payappSourceId is a stable id assigned to a Step-2 payapp row before merge matching runs, so a
  // subtotal's anchor can be traced through exact/fuzzy/no-match resolution to whichever row survives.
  // __fuzzyProjectSideId is set on the payapp-side of a fuzzy pair to the project row's id — that id is
  // what mergedItem.id becomes once the pair is actually merged (see autoMergeFuzzyMatch/handleMergeFuzzyMatch),
  // so it's the correct anchor target whether or not the merge has happened yet.
  __payappSourceId?: string;
  __fuzzyProjectSideId?: string;
};

export type BudgetSummaryDTO = {
  originalContractBudgetSum: number;
  reallocationsChangeOrders: number;
  contractBudgetSumToDate: number;
  totalCompletedAndStored: number;
  totalRetainageHeld: number;
  totalRetainageHeldPercentage: number;
  totalEarnedLessRetainage: number;
  lessPreviousFundings: number;
  currentPaymentDue: number;
  balanceToCompletePlusRetainage: number;
  adjustmentsByCDCC: number;
  totalAmountReccForFunding: number;
  requestedAmount: number;
  difference: number;
  estRetainageReleasedThisPeriod: number;
  totalContractBudgetPerComplete: number;
  // ... (any other fields as per your model)
};

export type ProjectInfoDTO = {
  projectId: string;
  projectNumber: string;
  projectTitle: string;
  projectAddress: ProjectAddress;
  projectTypeDescription: string | null;
  client?: PersonDTO;
  commencementDate: string;
  revisedCompletionDate: string;
  createdOn?: string;
  createdBy?: UserDTO;
  revisedBudgetSum?: number;
  elevatorCount?: number;
};
export type ProjectInfoIndexDTO = {
  Id: string;
  ProjectNumber: string;
  ProjectTitle: string;
  ProjectAddress: ProjectAddress;
  ProjectTypeDescription: string | null;
  CommencementDate: string;
  RevisedCompletionDate: string;
  CreatedOn?: string;
  CreatedBy?: string;
};

export type ProjectInfoListResponse = {
  projectsList: ProjectInfoDTO[];
  total: number;
};

export type RecentlyUploadedProjectsDTO = {
  id: string;
  projectNumber: string;
  projectTitle: string;
  createdOn: string;
  updatedOn: string;
  createdBy?: UserDTO;
};
export type LinkedReportsDTO = {
  id: string;
  reportName: string;
};
export type LineItemDTO = {
  id: string;
  description: string;
  unit?: string | null;
  divisionId?: string | null;
  divisionCode?: string | null;
  divisionName?: string | null;
  score?: number;
  isExactMatch?: boolean;
};
export type ProjectDraw = {
  id: string;
  name: string;
  periodBeginning?: string;
  periodEnding?: string;
  project?: ProjectInfoDTO;
  isEnabled?: boolean;
};

export type PayappRow = {
  [key: string]: any;
};

type LookupOption = {
  value: string;
  label: string;
};
export type BasicProjectInfoDTO = {
  projectNumber?: string;
  projectTitle?: string;
  projectAddress?: ProjectAddress;
};
export type OtherInfo = {
  contractor: string | null;
  loanOfficerName: string | null;
  observer: UserDTO | null;
  monitoringFee: number | null;
  monitoringType: string | null;
  planOrCostFee: number | null;
  planOrCostInvDate: string | null;
  siteArea: number | null;
  storyCount: number | null;
  buildingCount: number | null;
  unitCount: number | null;
  elevatorCount: number | null;
  otherCostFactor: string | null;
  grossBuildingArea: number | null;
  netArea: number | null;
  planOrCostReportDate: string | null;
  commencementDate: string | null;
  originalCompletionDateEstimated: string | null;
  revisedCompletionDate: string | null;
  bankLoanExpirationDate: string | null;
  constructionType: string | null;
  electrical: string | null;
  exteriorWalls: string | null;
  foundation: string | null;
  hVACScope: string | null;
  hVACType: string | null;
  plumbing: string | null;
  projectType: string | null;
  roof: string | null;
  superStructure: string | null;
  uniqueCostFactors1: string | null;
  uniqueCostFactors2: string | null;
  uniqueCostFactors3: string | null;
  windows: string | null;
  reviewingSubcontractorLienWaivers: boolean | null;
  reviewingConstructionMeetingMinutes: boolean | null;
  reviewingPotentialChangeOrders: boolean | null;
  reviewingPlanOrCost: boolean | null;
  networkerName: string | null;
  networkerStatus: string | null;
};

export interface CreateProjectDTO {
  projectInfo: BasicProjectInfoDTO;
  client: PersonDTO;
  borrower: PersonDTO;
  otherInfo: OtherInfo;
}

export type PayappHeaderMappingDTO = {
  sourceHeader: string;
  targetHeader: string | null;
};

const uniqueCostFactorsLookupOptions: LookupOption[] = [
  { value: 'High end quality project', label: 'High end quality project' },
  { value: 'Low end quality project', label: 'Low end quality project' },
  { value: 'No Detention facility', label: 'No Detention facility' },
  { value: 'Larger than normal detention facility', label: 'Larger than normal detention facility' },
  { value: 'Expensive excavation through rock', label: 'Expensive excavation through rock' },
  { value: 'Extensive excavation required', label: 'Extensive excavation required' },
  { value: 'Includes Significant Off-site utility work', label: 'Includes Significant Off-site utility work' },
  { value: 'Includes attached parking structure', label: 'Includes attached parking structure' },
  { value: 'Includes detached parking structure', label: 'Includes detached parking structure' },
  { value: 'Shell work only', label: 'Shell work only' },
  { value: 'Large glazing component', label: 'Large glazing component' },
  { value: 'Large masonry component', label: 'Large masonry component' },
  { value: 'Complex building form', label: 'Complex building form' },
];

export interface ProjectFilterConfigProps {
  stateOptions?: FilterConfigLookupOption[];
  cityOptions?: FilterConfigLookupOption[];
  countyOptions?: FilterConfigLookupOption[];
  customAreaOptions?: FilterConfigLookupOption[];
  userOptions?: FilterConfigLookupOption[];
}

export const getProjectFiltersConfig = (props?: ProjectFilterConfigProps): FilterConfig[] => {
  return [
    {
      propertyName: 'ProjectNumber',
      propertyDisplayName: 'Project Number',
      type: 'text',
      multiSelect: true,
      onDisplay: false,
    },
    {
      propertyName: 'ProjectTitle',
      propertyDisplayName: 'Project Title',
      type: 'text',
      multiSelect: true,
      onDisplay: false,
    },
    {
      propertyName: 'ProjectTags',
      propertyDisplayName: 'Tags',
      type: 'lookup',
      onDisplay: true,
      multiSelect: true,
      nested: true,
      lookupOptions: [
        { label: ProjectTags.Draft, value: ProjectTags.Draft },
        { label: ProjectTags.DataMissing, value: ProjectTags.DataMissing },
        { label: ProjectTags.CautionNeeded, value: ProjectTags.CautionNeeded },
        { label: ProjectTags.BadBudget, value: ProjectTags.BadBudget },
        { label: ProjectTags.BadProject, value: ProjectTags.BadProject },
      ],
    },
    {
      propertyName: 'Contractor',
      propertyDisplayName: 'Contractor',
      type: 'text',
      multiSelect: true,
    },
    {
      propertyName: 'Observer',
      propertyDisplayName: 'Observer',
      type: 'lookup',
      multiSelect: true,
      lookupOptions: props?.userOptions ?? [],
    },
    {
      propertyName: 'MonitoringFee',
      propertyDisplayName: 'Monitoring Fee',
      type: 'number',
      multiSelect: true,
    },
    {
      propertyName: 'MonitoringType',
      propertyDisplayName: 'Monitoring Type',
      type: 'lookup',
      multiSelect: true,
      lookupOptions: [
        { label: 'Limited', value: 'Limited' },
        { label: 'Magic', value: 'Magic' },
        { label: 'None', value: 'None' },
      ],
    },
    {
      propertyName: 'PlanOrCostFee',
      propertyDisplayName: 'Plan or Cost Fee',
      type: 'number',
      multiSelect: true,
    },
    {
      propertyName: 'OriginalCompletionDateEstimated',
      propertyDisplayName: 'Original Completion Date',
      type: 'date',
      // onDisplay: true,
    },
    {
      propertyName: 'RevisedCompletionDate',
      propertyDisplayName: 'Revised Completion Date',
      type: 'date',
    },
    {
      propertyName: 'BankLoanExpirationDate',
      propertyDisplayName: 'Bank Loan Expiration Date',
      type: 'date',
    },
    {
      propertyName: 'PlanOrCostInvDate',
      propertyDisplayName: 'Plan or Cost Inv Date',
      type: 'date',
    },
    {
      propertyName: 'PlanOrCostReportDate',
      propertyDisplayName: 'Plan or Cost Report Date',
      type: 'date',
    },
    {
      propertyName: 'CreatedOn',
      propertyDisplayName: 'Uploaded On',
      type: 'date',
    },
    {
      propertyName: 'CreatedBy',
      propertyDisplayName: 'Uploaded By',
      type: 'text',
      multiSelect: true,
    },
    {
      propertyName: 'City',
      propertyDisplayName: 'City',
      type: 'lookup',
      onDisplay: true,
      multiSelect: true,
      lookupOptions: props?.cityOptions ?? [],
      displayedOptionsLimit: 10,
      group: 'Location',
    },
    {
      propertyName: 'County',
      propertyDisplayName: 'County',
      type: 'lookup',
      onDisplay: true,
      multiSelect: true,
      lookupOptions: props?.countyOptions ?? [],
      displayedOptionsLimit: 10,
      group: 'Location',
    },
    {
      propertyName: 'CustomArea',
      propertyDisplayName: 'Area',
      type: 'lookup',
      onDisplay: true,
      multiSelect: true,
      lookupOptions: props?.customAreaOptions ?? [],
      displayedOptionsLimit: 10,
      group: 'Location',
    },
    {
      propertyName: 'State',
      propertyDisplayName: 'State',
      type: 'lookup',
      onDisplay: true,
      multiSelect: true,
      lookupOptions: props?.stateOptions ?? [],
      group: 'Location',
    },
    {
      propertyName: 'PostalCode',
      propertyDisplayName: 'Postal Code',
      type: 'text',
      onDisplay: true,
      multiSelect: true,
      group: 'Location',
    },
    {
      propertyName: 'ProjectType',
      propertyDisplayName: 'Project Type',
      type: 'lookup',
      multiSelect: true,
      onDisplay: true,
      lookupOptions: [
        { label: 'Apartments and Condos', value: 'Apartments and Condos' },
        { label: 'Garden Apartments', value: 'Garden Apartments' },
        { label: 'Urban Apartments (with a Podium Deck)', value: 'Urban Apartments (with a Podium Deck)' },
        { label: 'Urban Apartments (Wrapped around Garage)', value: 'Urban Apartments (Wrapped around Garage)' },
        { label: 'Convenience Stores', value: 'Convenience Stores' },
        { label: 'Car Washes', value: 'Car Washes' },
        { label: 'Assisted Living Facilities', value: 'Assisted Living Facilities' },
        { label: 'Hospitals and Clinics', value: 'Hospitals and Clinics' },
        { label: 'Hotels', value: 'Hotels' },
        { label: 'Medical Office Buildings', value: 'Medical Office Buildings' },
        { label: 'Office Buildings', value: 'Office Buildings' },
        { label: 'Parking Garages', value: 'Parking Garages' },
        { label: 'Churches and Religious Projects', value: 'Churches and Religious Projects' },
        { label: 'Restaurants', value: 'Restaurants' },
        { label: 'Retail Projects', value: 'Retail Projects' },
        { label: 'Schools and University Projects', value: 'Schools and University Projects' },
        { label: 'Storage Facilities', value: 'Storage Facilities' },
        { label: 'Theaters', value: 'Theaters' },
        { label: 'Office / Warehouse Projects', value: 'Office / Warehouse Projects' },
        { label: 'Light Manufacturing Warehouse Projects', value: 'Light Manufacturing Warehouse Projects' },
        { label: 'Breweries and Distribution Projects', value: 'Breweries and Distribution Projects' },
        { label: 'Custom Home / Residential Home', value: 'Custom Home / Residential Home' },
        { label: 'Land Development', value: 'Land Development' },
      ],
    },
    {
      propertyName: 'Status',
      propertyDisplayName: 'Status',
      type: 'lookup',
      onDisplay: true,
      multiSelect: true,
      lookupOptions: [
        { label: 'Active', value: 'Active' },
        { label: 'Idle', value: 'Idle' },
        { label: 'Closed', value: 'Closed' },
        { label: 'PCR', value: 'PCR' },
        { label: 'PCA/Single Report', value: 'PCA/Single Report' },
      ],
    },
    {
      propertyName: 'NetworkerStatus',
      propertyDisplayName: 'Networker',
      type: 'lookup',
      multiSelect: true,
      lookupOptions: [
        { label: 'True', value: 'TRUE' },
        { label: 'Partial', value: 'PARTIAL' },
        { label: 'False', value: 'FALSE' },
      ],
    },
    {
      propertyName: 'Budget',
      propertyDisplayName: 'Budget',
      type: 'number',
      rangeMin: 0,
      rangeMax: 100000000,
      onDisplay: true,
    },
    {
      propertyName: 'UnassignedBudget',
      propertyDisplayName: 'Unassigned Budget',
      type: 'number',
      rangeMin: 0,
      rangeMax: 100000000,
      onDisplay: false,
    },
    {
      propertyName: 'CommencementDate',
      propertyDisplayName: 'Commencement Date',
      type: 'date',
      // onDisplay: true,
    },
    {
      propertyName: 'ConstructionType',
      propertyDisplayName: 'Construction Type',
      type: 'lookup',
      multiSelect: true,
      lookupOptions: [
        { label: 'New Construction', value: 'New Construction' },
        { label: 'Remodel or Rehab', value: 'Remodel or Rehab' },
        { label: 'Mix New and Rehab', value: 'Mix New and Rehab' },
      ],
    },
    {
      propertyName: 'Electrical',
      propertyDisplayName: 'Electrical',
      type: 'lookup',
      multiSelect: true,
      lookupOptions: [
        { label: 'None - exterior but not shell space', value: 'None - exterior but not shell space' },
        { label: 'Shell space rough power and light', value: 'Shell space rough power and light' },
        { label: 'Space fully powered and fixtured', value: 'Space fully powered and fixtured' },
      ],
    },
    {
      propertyName: 'ExteriorWalls',
      propertyDisplayName: 'Exterior Walls',
      type: 'lookup',
      multiSelect: true,
      lookupOptions: [
        { label: 'Masonry Veneer / brick, stone, stucco, EIFS', value: 'Masonry Veneer / brick, stone, stucco, EIFS' },
        { label: 'Siding / wood or cementous', value: 'Siding / wood or cementous' },
        { label: 'Curtainwall', value: 'Curtainwall' },
        { label: 'Paint on base superstructure', value: 'Paint on base superstructure' },
        { label: 'Metal or Other Panels', value: 'Metal or Other Panels' },
      ],
    },
    {
      propertyName: 'Foundation',
      propertyDisplayName: 'Foundation',
      type: 'lookup',
      multiSelect: true,
      lookupOptions: [
        { label: 'Piers with Slab and Base', value: 'Piers with Slab and Base' },
        { label: 'Slab on Grade - Ground Supported', value: 'Slab on Grade - Ground Supported' },
        { label: 'Slab on Grade - Pier Supported', value: 'Slab on Grade - Pier Supported' },
        { label: 'Conventional / Shallow Foundations', value: 'Conventional / Shallow Foundations' },
        { label: 'Deep Foundation', value: 'Deep Foundation' },
        { label: 'Special Foundation', value: 'Special Foundation' },
      ],
    },
    {
      propertyName: 'HVACScope',
      propertyDisplayName: 'HVAC Scope',
      type: 'lookup',
      multiSelect: true,
      lookupOptions: [
        { label: 'None - Unconditioned shell space', value: 'None - Unconditioned shell space' },
        { label: 'Conditioned shell space', value: 'Conditioned shell space' },
        { label: 'Conditioned complete finish out', value: 'Conditioned complete finish out' },
      ],
    },
    {
      propertyName: 'HVACType',
      propertyDisplayName: 'HVAC Type',
      type: 'lookup',
      multiSelect: true,
      lookupOptions: [
        { label: 'Split refrigerant system (gas or elec)', value: 'Split refrigerant system (gas or elec)' },
        { label: 'Packaged refrigerant system (gas or elec)', value: 'Packaged refrigerant system (gas or elec)' },
        { label: 'VAV / Chilled Water system', value: 'VAV / Chilled Water system' },
      ],
    },
    {
      propertyName: 'Plumbing',
      propertyDisplayName: 'Plumbing',
      type: 'lookup',
      multiSelect: true,
      lookupOptions: [
        { label: 'None - shell space', value: 'None - shell space' },
        { label: 'Plumbed rough shell space', value: 'Plumbed rough shell space' },
        { label: 'Plumbed complete finish out', value: 'Plumbed complete finish out' },
      ],
    },
    {
      propertyName: 'Roof',
      propertyDisplayName: 'Roof',
      type: 'lookup',
      multiSelect: true,
      lookupOptions: [
        { value: 'EPDM / TPO / Membrane Roofing', label: 'EPDM / TPO / Membrane Roofing' },
        { value: 'Built Up / Bituminous Roofing', label: 'Built Up / Bituminous Roofing' },
        { value: 'Wood or Compostion Shingle Roofing', label: 'Wood or Compostion Shingle Roofing' },
        { value: 'Clay or Concrete Tile Roofing', label: 'Clay or Concrete Tile Roofing' },
        { value: 'Metal Roofing', label: 'Metal Roofing' },
      ],
    },
    {
      propertyName: 'SuperStructure',
      propertyDisplayName: 'Super Structure',
      type: 'lookup',
      multiSelect: true,
      lookupOptions: [
        { value: 'Wood studs, joists, rafters, trusses', label: 'Wood studs, joists, rafters, trusses' },
        { value: 'Structural Steel (studs, beams, columns, trusses)', label: 'Structural Steel (studs, beams, columns, trusses)' },
        { value: 'Poured in Place Concrete', label: 'Poured in Place Concrete' },
        { value: 'Pre-Engineered Metal', label: 'Pre-Engineered Metal' },
        { value: 'Precast Concrete', label: 'Precast Concrete' },
        { value: 'Structural Masonry / steel infill', label: 'Structural Masonry / steel infill' },
        { value: 'Tilt Wall Concrete / steel infill', label: 'Tilt Wall Concrete / steel infill' },
      ],
    },
    {
      propertyName: 'UniqueCostFactors1',
      propertyDisplayName: 'Unique Cost Factors 1',
      type: 'lookup',
      multiSelect: true,
      lookupOptions: uniqueCostFactorsLookupOptions,
    },
    {
      propertyName: 'UniqueCostFactors2',
      propertyDisplayName: 'Unique Cost Factors 2',
      type: 'lookup',
      multiSelect: true,
      lookupOptions: uniqueCostFactorsLookupOptions,
    },
    {
      propertyName: 'UniqueCostFactors3',
      propertyDisplayName: 'Unique Cost Factors 3',
      type: 'lookup',
      multiSelect: true,
      lookupOptions: uniqueCostFactorsLookupOptions,
    },
    {
      propertyName: 'OtherCostFactor',
      propertyDisplayName: 'Other Cost Factor',
      type: 'text',
      multiSelect: true,
    },
    {
      propertyName: 'Windows',
      propertyDisplayName: 'Windows',
      type: 'lookup',
      multiSelect: true,
      lookupOptions: [
        { value: 'Individual openings', label: 'Individual openings' },
        { value: 'Storefront', label: 'Storefront' },
        { value: 'Curtainwall', label: 'Curtainwall' },
      ],
    },
    {
      propertyName: 'SiteArea',
      propertyDisplayName: 'Site Area',
      type: 'number',
      rangeMin: 0,
      rangeMax: 1000,
    },
    {
      propertyName: 'StoryCount',
      propertyDisplayName: 'Story Count',
      type: 'number',
      rangeMin: 0,
      rangeMax: 10,
    },
    {
      propertyName: 'BuildingCount',
      propertyDisplayName: 'Building Count',
      type: 'number',
      rangeMin: 0,
      rangeMax: 50,
    },
    {
      propertyName: 'UnitCount',
      propertyDisplayName: 'Unit Count',
      type: 'number',
      rangeMin: 0,
      rangeMax: 1000,
    },
    {
      propertyName: 'ElevatorCount',
      propertyDisplayName: 'Elevator Count',
      type: 'number',
      rangeMin: 0,
      rangeMax: 10,
    },
    {
      propertyName: 'GrossBuildingArea',
      propertyDisplayName: 'Gross Building Area',
      type: 'number',
      rangeMin: 0,
      rangeMax: 100000,
    },
    {
      propertyName: 'NetArea',
      propertyDisplayName: 'Net Area',
      type: 'number',
      rangeMin: 0,
      rangeMax: 100000,
    },
    {
      propertyName: 'Client',
      propertyDisplayName: 'Client',
      type: 'text',
      multiSelect: true,
      onDisplay: true,
    },
    {
      propertyName: 'Borrower',
      propertyDisplayName: 'Borrower',
      type: 'text',
      multiSelect: true,
      onDisplay: true,
    },
    // Add more configurations as needed for other fields
  ];
};

const lookupOptionsForProperty = (propertyName: string) => {
  const configItem = getProjectFiltersConfig().find(config => config.propertyName === propertyName);
  // Since you're sure that lookupOptions exists, you can directly spread them.
  // However, it's still a good practice to keep a fallback empty array.
  return [{ value: 'None', label: '' }, ...(configItem?.lookupOptions || [])];
};

export const statusOptions = lookupOptionsForProperty('Status');
export const projectTypeOptions = lookupOptionsForProperty('ProjectType');
export const constructionTypeOptions = lookupOptionsForProperty('ConstructionType');
export const electricalOptions = lookupOptionsForProperty('Electrical');
export const exteriorWallsOptions = lookupOptionsForProperty('ExteriorWalls');
export const foundationOptions = lookupOptionsForProperty('Foundation');
export const hvacScopeOptions = lookupOptionsForProperty('HVACScope');
export const hvacTypeOptions = lookupOptionsForProperty('HVACType');
export const plumbingOptions = lookupOptionsForProperty('Plumbing');
export const roofOptions = lookupOptionsForProperty('Roof');
export const superStructureOptions = lookupOptionsForProperty('SuperStructure');
export const uniqueCostFactors1Options = lookupOptionsForProperty('UniqueCostFactors1');
export const uniqueCostFactors2Options = lookupOptionsForProperty('UniqueCostFactors2');
export const uniqueCostFactors3Options = lookupOptionsForProperty('UniqueCostFactors3');
export const windowsOptions = lookupOptionsForProperty('Windows');
export const monitoringTypeOptions = lookupOptionsForProperty('MonitoringType');

export const fieldNameMapping: { [key: string]: string[] } = {
  // DTO Field : Frontend Field
  'Project Title': ['Project Title'],
  'Project #': ['Project Number'],
  Contractor: ['Contractor'],
  Observer: ['Observer'],
  'Loan Officer Name': ['Loan Officer Name'],
  'Monitoring Fee': ['Monitoring Fee'],
  'Monitoring Type': ['Monitoring Type'],
  'Plan / Cost Fee': ['Plan / Cost Review Fee'],
  'Site Area (ac)': ['Site Area (ac)', 'Site Acreage'],
  'Story Cnt. (most common)': ['Story Count (most common)', '# Stories'],
  'Building Count': ['Building Count', '# Apt Buildings'],
  'Unit Count': ['Unit Count', '# Units'],
  'No. of Elevator stops': ['No. of Elevator Stops'],
  'Elevator Count': ['No. of Elevator Stops'],
  'Other Cost Factor': ['OtherCostFactor'],
  'Gross Building Area (sf)': ['Gross Building Area (SF)', 'Gross Building SF'],
  'Net Area (sf)': ['Net Area (SF)', 'Net Rentable SF'],
  'Plan / Cost Inv. Date': ['Plan / Cost Review Invoice Date'],
  'Plan/Cost Report Date': ['Plan or Cost Report Date'],
  ProjectType: ['Project Type'],
  SuperStructure: ['Superstructure'],
  ExteriorWalls: ['Exterior Walls'],
  Roof: ['Roof'],
  HVACScope: ['HVAC Scope'],
  HVACType: ['HVAC Type'],
  Plumbing: ['Plumbing'],
  Electrical: ['Electrical'],
  Windows: ['Windows'],
  ConstructionType: ['Construction Type'],
  UniqueCostFactors1: ['Unique Cost Factors 1'],
  UniqueCostFactors2: ['Unique Cost Factors 2'],
  UniqueCostFactors3: ['Unique Cost Factors 3'],
  Foundation: ['Foundation'],
  'Commencement Date': ['Commencement Date'],
  'Original Completion Date': ['Original Completion Date'],
  'Revised Completion Date': ['Revised Completion Date'],
  'Bank Loan Expiration Date': ['Bank Loan Expiration Date'],
  'CDCC-Estimated Completion Date': ['CDCC-Estimated Completion Date'],
  'Period Beginning': ['Period Beginning'],
  'Period Ending': ['Period Ending'],
  'Application Number': ['Application Number'],
  'Less Previous Fundings': ['Less Previous Fundings', 'Previous Fundings'], // Multiple mappings
  'Adjustments by CDCC': ['Adjustments by CDCC'],
  'Requested Amount': ['Requested Amount'],
  'Est. Retainage Released This Period': ['Est. Retainage Released This Period'],
};
export const projectFieldsToHighlight =
  'ProjectTitle,ProjectNumber,CreatedBy,ProjectType,City,Contractor,County,CustomArea,PostalCode,ConstructionType,Electrical,ExteriorWalls,Foundation,HVACScope,HVACType,Roof,Superstructure,UniqueCostFactors1,UniqueCostFactors2,UniqueCostFactors3,OtherCostFactor,Windows,Borrower,Client,Observer,Status,ProjectTags';
